import axios from 'axios';
import { v2Api, API_ROOT } from './v2Client';
import { ensureAccessToken } from './authTokens';
import { clubRequestHeaders } from '../currentClub';

const LOG = '[SquashAPI shop]';

/**
 * Boutique du club (voir Controllers/ShopAdminController.cs et docs/SHOP.md côté API). Lecture et suivi des
 * commandes (prête, retirée) : staff. Catalogue et annulation / remboursement : administrateur (rôle 1), 403 sinon.
 */

export const SHOP_CATEGORIES = [
  { id: 'abonnements', label: 'Abonnements' },
  { id: 'raquettes', label: 'Raquettes' },
  { id: 'balles', label: 'Balles' },
  { id: 'grips', label: 'Grips' },
  { id: 'sacs', label: 'Sacs' },
  { id: 'textile', label: 'Textile' },
  { id: 'chaussures', label: 'Chaussures' },
  { id: 'accessoires', label: 'Accessoires' },
];

/** Catégorie des abonnements (articles kind = "membership") : forcée par l'API, interdite aux autres articles. */
export const MEMBERSHIP_CATEGORY = 'abonnements';

/** Catégories proposées pour un article ordinaire (sans « Abonnements », réservée aux abonnements). */
export const SHOP_ARTICLE_CATEGORIES = SHOP_CATEGORIES.filter((c) => c.id !== MEMBERSHIP_CATEGORY);

export const SHOP_ORDER_STATUS_LABELS = {
  pending_payment: 'Paiement en cours',
  paid: 'À préparer',
  ready: 'Prête au club',
  collected: 'Retirée',
  cancelled: 'Annulée',
};

/**
 * Libellé du statut d'une commande. Commande d'abonnements seuls (`pickup` faux) : rien à retirer au club, donc
 * « Terminée » une fois close et « À encaisser au club » tant qu'un abonnement payable au club attend son paiement.
 */
export function shopOrderStatusLabel(order) {
  const status = order?.status;
  if (order?.pickup === false) {
    if (status === 'collected') return 'Terminée';
    if ((status === 'paid' || status === 'ready') && Number(order.on_site_due_cents) > 0) return 'À encaisser au club';
  }
  return SHOP_ORDER_STATUS_LABELS[status] || status || '';
}

const MEMBERSHIP_LINE_STATUS_LABELS = {
  active: 'Abonnement actif',
  pending_approval: 'Abonnement à valider',
  rejected: 'Abonnement refusé',
  cancelled: 'Abonnement annulé',
  expired: 'Abonnement expiré',
};

/** Statut de l'abonnement créé par une ligne de commande ; « à payer » précise au club ou en ligne (selon la commande). */
export function membershipLineStatusLabel(line, order) {
  const status = line?.membership?.status;
  if (!status) return '';
  if (status === 'pending_payment') return Number(order?.on_site_amount_cents) > 0 ? 'Abonnement à payer au club' : 'Abonnement à payer en ligne';
  return MEMBERSHIP_LINE_STATUS_LABELS[status] || status;
}

/** Moyens de paiement acceptés par article (API : payment_methods). */
export const SHOP_PAYMENT_METHODS = [
  { id: 'online', label: 'En ligne', hint: 'carte, Apple Pay, Google Pay' },
  { id: 'wallet', label: 'Crédit wallet', hint: 'solde du joueur' },
  { id: 'on_site', label: 'Au club', hint: 'payé au retrait' },
];

export function shopErrorMessage(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

/** Couleur { name, hex } — ancien format (simple code "#111111") toléré. */
function normalizeColor(c) {
  if (typeof c === 'string') return { name: c, hex: /^#[0-9a-f]{6}$/i.test(c) ? c : null };
  return { name: String(c?.name ?? ''), hex: c?.hex ?? null };
}

/** Date de l'API ("AAAA-MM-JJ…") → "AAAA-MM-JJ", ou null. */
function day(value) {
  const v = value ? String(value).slice(0, 10) : '';
  return /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null;
}

/** Partie « abonnement » d'un article kind = "membership" (null pour un article ordinaire). */
function normalizeProductMembership(m) {
  if (!m) return null;
  return {
    ...m,
    plan_id: m.plan_id != null ? String(m.plan_id) : null,
    period_start: day(m.period_start),
    period_end: day(m.period_end),
    sales_open_from: day(m.sales_open_from),
    sales_close_at: day(m.sales_close_at),
    requires_approval: Boolean(m.requires_approval),
    wallet_credit_cents: Number(m.wallet_credit_cents ?? 0) || 0,
    court_discount_percent: Number(m.court_discount_percent ?? 0) || 0,
    // Sports de la réduction terrain (codes) ; vide = tous les sports.
    court_discount_sports: Array.isArray(m.court_discount_sports) ? m.court_discount_sports.map(String) : [],
    shop_discount_percent: Number(m.shop_discount_percent ?? 0) || 0,
    // Remise boutique par catégorie d'article (id → %), prioritaire sur la remise générale.
    shop_discount_categories: m.shop_discount_categories && typeof m.shop_discount_categories === 'object' ? { ...m.shop_discount_categories } : {},
    badge_label: m.badge_label ?? '',
    max_members: m.max_members ?? null,
    remaining: m.remaining ?? null,
    code: m.code ?? '',
    subscription_count: Number(m.subscription_count ?? 0) || 0,
    active_count: Number(m.active_count ?? 0) || 0,
    pending_payment_count: Number(m.pending_payment_count ?? 0) || 0,
    pending_approval_count: Number(m.pending_approval_count ?? 0) || 0,
    pricing_locked: Boolean(m.pricing_locked),
  };
}

/** Ligne de commande : kind, remboursement de la ligne seule, état de l'abonnement créé (null pour un article). */
function normalizeOrderLine(l) {
  const m = l?.membership;
  return {
    ...l,
    kind: l?.kind === 'membership' ? 'membership' : 'article',
    refunded_cents: Number(l?.refunded_cents ?? 0) || 0,
    membership: m
      ? {
          ...m,
          subscription_id: m.subscription_id != null ? String(m.subscription_id) : null,
          period_start: day(m.period_start),
          period_end: day(m.period_end),
          rejected_reason: m.rejected_reason ?? null,
        }
      : null,
  };
}

export function normalizeProduct(p) {
  const kind = p.kind === 'membership' ? 'membership' : 'article';
  return {
    ...p,
    id: String(p.id),
    price_cents: Number(p.price_cents ?? 0) || 0,
    compare_at_cents: p.compare_at_cents ?? null,
    stock: p.stock ?? null,
    sold_count: Number(p.sold_count ?? 0) || 0,
    images: Array.isArray(p.images) ? p.images.filter((i) => i?.url) : p.image_url ? [{ url: p.image_url, color: null }] : [],
    colors: Array.isArray(p.colors) ? p.colors.map(normalizeColor).filter((c) => c.name) : [],
    sizes: Array.isArray(p.sizes) ? p.sizes : [],
    // Déclinaisons couleur × taille : stock null = non suivi.
    variants: Array.isArray(p.variants)
      ? p.variants.map((v) => ({
          id: String(v.id),
          color: v.color ?? null,
          size: v.size ?? null,
          stock: v.stock ?? null,
          active: v.active !== false,
          sold_count: Number(v.sold_count ?? 0) || 0,
        }))
      : [],
    track_stock: Boolean(p.track_stock),
    payment_methods: Array.isArray(p.payment_methods) ? p.payment_methods : ['online', 'wallet'],
    out_of_stock_variants: Number(p.out_of_stock_variants ?? 0) || 0,
    active: Boolean(p.active),
    trending: Boolean(p.trending),
    kind,
    membership: kind === 'membership' ? normalizeProductMembership(p.membership) : null,
  };
}

export function normalizeOrder(o) {
  return {
    ...o,
    id: String(o.id),
    total_cents: Number(o.total_cents ?? 0) || 0,
    // Remise abonné (figée à la commande) : sous-total des lignes, %, montant, abonnement.
    subtotal_cents: Number(o.subtotal_cents ?? o.total_cents ?? 0) || 0,
    discount_percent: Number(o.discount_percent ?? 0) || 0,
    discount_cents: Number(o.discount_cents ?? 0) || 0,
    discount_label: o.discount_label || null,
    wallet_paid_cents: Number(o.wallet_paid_cents ?? 0) || 0,
    wallet_refunded_cents: Number(o.wallet_refunded_cents ?? 0) || 0,
    card_amount_cents: Number(o.card_amount_cents ?? 0) || 0,
    card_refunded_cents: Number(o.card_refunded_cents ?? 0) || 0,
    refundable_cents: Number(o.refundable_cents ?? 0) || 0,
    on_site_amount_cents: Number(o.on_site_amount_cents ?? 0) || 0,
    on_site_due_cents: Number(o.on_site_due_cents ?? 0) || 0,
    // Faux = abonnements seuls : rien à préparer ni à retirer au club.
    pickup: o.pickup !== false,
    lines: Array.isArray(o.lines) ? o.lines.map(normalizeOrderLine) : [],
  };
}

async function post(path, payload, what) {
  try {
    const { data } = await v2Api.post(path, payload);
    return data;
  } catch (err) {
    console.error(LOG, `${what} échoué`, { payload, err });
    throw err;
  }
}

export async function listShopProducts() {
  const { data } = await v2Api.get('/ShopAdmin/products');
  return (Array.isArray(data) ? data : []).map(normalizeProduct);
}

export async function upsertShopProduct(payload) {
  return normalizeProduct(await post('/ShopAdmin/product_upsert', payload, 'product_upsert'));
}

/** Stock par déclinaison : items [{ id, delta }] (réception) ou [{ id, stock, expected }] (inventaire). */
export async function adjustShopVariantStock(productId, items) {
  return normalizeProduct(await post('/ShopAdmin/variant_stock', { product_id: String(productId), items }, 'variant_stock'));
}

export async function archiveShopProduct(id, active) {
  return normalizeProduct(await post('/ShopAdmin/product_archive', { id: String(id), active }, 'product_archive'));
}

/** Photo d'article → URL publique (à enregistrer ensuite dans image_url). */
export async function uploadShopImage(file) {
  const fd = new FormData();
  fd.append('file', file);
  const access = await ensureAccessToken(API_ROOT);
  const headers = clubRequestHeaders();
  if (access) headers.Authorization = `Bearer ${access}`;
  try {
    const { data } = await axios.post(`${API_ROOT}/ShopAdmin/upload_image`, fd, {
      headers,
      maxContentLength: 12 * 1024 * 1024,
      maxBodyLength: 12 * 1024 * 1024,
    });
    return data?.url ?? null;
  } catch (err) {
    console.error(LOG, 'upload_image échoué', err);
    throw err;
  }
}

/** status : exact, ou "open" = à préparer + prêtes. playerId : commandes d'un seul joueur. category : commandes
 * contenant au moins un article de cette catégorie (SHOP_CATEGORIES). */
export async function listShopOrders({ status, search, playerId, category, page = 1, pageSize = 50 } = {}) {
  const params = { page, page_size: pageSize };
  if (status) params.status = status;
  if (search) params.search = search;
  if (category) params.category = category;
  if (playerId) params.player_id = String(playerId);
  try {
    const { data } = await v2Api.get('/ShopAdmin/orders', { params });
    return {
      items: (Array.isArray(data?.items) ? data.items : []).map(normalizeOrder),
      total: data?.total ?? 0,
      summary: data?.summary ?? { to_prepare_count: 0, ready_count: 0, collected_cents: 0 },
    };
  } catch (err) {
    console.error(LOG, 'orders échoué', { params, err });
    throw err;
  }
}

export async function getShopOrder(id) {
  const { data } = await v2Api.get(`/ShopAdmin/orders/${encodeURIComponent(id)}`);
  return normalizeOrder(data);
}

export async function setShopOrderStatus(id, status, note) {
  return normalizeOrder(await post('/ShopAdmin/order_status', { id: String(id), status, note: note || null }, 'order_status'));
}

export async function cancelShopOrder(id, { reason, refund = true }) {
  return normalizeOrder(await post('/ShopAdmin/order_cancel', { id: String(id), reason, refund }, 'order_cancel'));
}

/** Icônes décoratives proposées pour les bannières (mêmes noms que ShopPolicy.BannerIcons côté API). */
export const SHOP_BANNER_ICONS = ['flash', 'pricetag', 'shirt', 'tennisball', 'trophy', 'gift', 'sparkles', 'flame', 'star', 'heart', 'bag-handle', 'rocket'];

function normalizeBanner(b) {
  return { ...b, id: String(b.id), active: Boolean(b.active), live: Boolean(b.live) };
}

export async function listShopBanners() {
  const { data } = await v2Api.get('/ShopAdmin/banners');
  return (Array.isArray(data) ? data : []).map(normalizeBanner);
}

export async function upsertShopBanner(payload) {
  return normalizeBanner(await post('/ShopAdmin/banner_upsert', payload, 'banner_upsert'));
}

export async function deleteShopBanner(id) {
  return post('/ShopAdmin/banner_delete', { id: String(id) }, 'banner_delete');
}

export async function reorderShopBanners(ids) {
  const data = await post('/ShopAdmin/banner_reorder', { ids: ids.map(String) }, 'banner_reorder');
  return (Array.isArray(data) ? data : []).map(normalizeBanner);
}

export const shopService = {
  listBanners: listShopBanners,
  upsertBanner: upsertShopBanner,
  deleteBanner: deleteShopBanner,
  reorderBanners: reorderShopBanners,
  listProducts: listShopProducts,
  upsertProduct: upsertShopProduct,
  archiveProduct: archiveShopProduct,
  adjustVariantStock: adjustShopVariantStock,
  uploadImage: uploadShopImage,
  listOrders: listShopOrders,
  getOrder: getShopOrder,
  setOrderStatus: setShopOrderStatus,
  cancelOrder: cancelShopOrder,
};
